import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheetRef } from 'react-spring-bottom-sheet';

import { OrganizationIcon } from '../../assets';
import { useFilterContext } from '../../contexts/FiltersContext';
import { inMobile, useWindowDimensions } from '../../hooks/useDimensions';
import { OrganizationsFilter } from '../../models/Filter';
import BaseFilterComponent from './BaseFilterComponent';
import { StyledButton, StyledSecondaryButton, StyledShimmerTitle } from './FiltersList.styles';
import { useSelectionFilter } from './hooks/SelectionFilter';
import SelectionFilter, { Value } from './SelectionFilter/SelectionFilter';
import { FilterFooterMobile } from './shared/FilterFooterMobile.styles';

interface OrganizationsFilterProps {
  sheetRef?: React.MutableRefObject<BottomSheetRef>;
}

export const OrganizationsFilterComponent = ({ sheetRef }: OrganizationsFilterProps) => {
  // Dependencies
  const { t } = useTranslation();

  const { width } = useWindowDimensions();
  const {
    handleApplyFilter,
    setOpenFiltersMobile,
    setSelectedFilterTitle,
    getSelectedFilter,
    getOrganizationFilterCount,
    setOrganizationsFilterData,
  } = useFilterContext();

  const [arrOptions, setArrOptions] = React.useState<Value[]>(null);
  const [isOrganizationCountLoading, setIsOrganizationCountLoading] = React.useState(false);
  const filter = getSelectedFilter(OrganizationsFilter) as OrganizationsFilter;

  const { optionValues, getFilter, onChangeMultiSelectionFnc, clearAllSelected } = useSelectionFilter({
    arrOptions,
    filter,
    constructor: (organizations) => new OrganizationsFilter({ values: organizations }),
  });

  // Effects
  React.useEffect(() => {
    void (async () => {
      try {
        setIsOrganizationCountLoading(true);
        const fetchedFiltersCount = await getOrganizationFilterCount();
        if (fetchedFiltersCount) {
          if (fetchedFiltersCount.length === 0) {
            // This timeout will adjust the height of bottomsheet when passing from shimmers to no option view
            setTimeout(() => {
              sheetRef.current?.snapTo(0);
            }, 100);
          }
          setArrOptions(
            fetchedFiltersCount.map((filter) => ({
              selected: filter.isSelected,
              value: filter.key,
              label: filter.value,
              data: filter.count,
            })) || [],
          );

          // Save the fetched data in localStorage so we can retrieve the org's name.
          // TODO: this is a provisory solution, we might want to migrate the saved data for string filters to be
          // { key: filter.key, value: filter.value } instead of value: string.
          setOrganizationsFilterData((prevState) => {
            // Process each item in fetchedFiltersCount
            const updatedData = prevState.map((prevFilter) => {
              const matchedObject = fetchedFiltersCount.find((filter) => filter.key === prevFilter.key);
              return { ...prevFilter, ...matchedObject };
            });

            return updatedData;
          });
        }
      } catch (error: any) {
        setArrOptions([]);
      } finally {
        setIsOrganizationCountLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers
  const applyOnClick = () => {
    if (!getFilter) return;
    const filter = getFilter();

    handleApplyFilter(filter);

    setTimeout(() => {
      setOpenFiltersMobile(false);
      setSelectedFilterTitle('');
    }, 100);
  };

  return (
    <>
      <SelectionFilter
        values={optionValues}
        onChange={onChangeMultiSelectionFnc}
        multiselect
        loading={isOrganizationCountLoading}
        searchInputPlaceholder={t('filters.searchFilterPlaceholder', { param: 'organizations' })}
        displayCount={!inMobile(width)}
        reverseCheckboxOrder={inMobile(width)}
        checkboxSizeInPx={inMobile(width) ? 24 : undefined}
      />
      {isOrganizationCountLoading ? (
        <FilterFooterMobile>
          <StyledShimmerTitle $flex="1" $height="3rem" $borderRadius="2.125rem" />
          <StyledShimmerTitle $flex="1" $height="3rem" $borderRadius="2.125rem" />
        </FilterFooterMobile>
      ) : (
        <FilterFooterMobile>
          <StyledSecondaryButton onPress={clearAllSelected}>{t('filters.clearAll')}</StyledSecondaryButton>
          <StyledButton disabled={arrOptions?.length === 0} onPress={applyOnClick}>
            {t('filters.applyFilter')}
          </StyledButton>
        </FilterFooterMobile>
      )}
    </>
  );
};
const AddOrganizationsFilter = () => {
  // Dependencies
  const { t } = useTranslation();
  const { getSelectedFilter, getOrganizationFilterCount, setOrganizationsFilterData, organizationsFilterData } =
    useFilterContext();

  const { width } = useWindowDimensions();

  // State
  const [arrOptions, setArrOptions] = React.useState<Value[]>(null);
  const [isOrganizationCountLoading, setIsOrganizationCountLoading] = React.useState(false);

  // Derived State
  const filter = getSelectedFilter(OrganizationsFilter) as OrganizationsFilter;

  const { optionValues, getFilter, onCancel, onChangeMultiSelectionFnc, clearAllSelected } = useSelectionFilter({
    arrOptions,
    filter,
    constructor: (organizations) => new OrganizationsFilter({ values: organizations }),
  });

  // Helpers
  const getOrgNameFromId = (orgId: string) => {
    return organizationsFilterData?.find((o) => o.key === orgId)?.value;
  };

  const printValues: string = filter?.values?.map((value) => getOrgNameFromId(value))?.join(', ');

  const handleOnOpenModal = async () => {
    try {
      setIsOrganizationCountLoading(true);
      const fetchedFiltersCount = await getOrganizationFilterCount();

      if (fetchedFiltersCount) {
        setArrOptions(
          fetchedFiltersCount.map((filter) => ({
            selected: filter.isSelected,
            value: filter.key,
            label: filter.value,
            data: filter.count,
          })) || [],
        );

        // Save the fetched data in localStorage so we can retrieve the org's name.
        // TODO: this is a provisory solution, we might want to migrate the saved data for string filters to be
        // { key: filter.key, value: filter.value } instead of value: string.
        setOrganizationsFilterData((prevState) => {
          const updatedData = fetchedFiltersCount.map((filter) => ({ key: filter.key, value: filter.value }));
          // Merge new data with existing data, eventually updating part of it.
          // This is done to prevent losing data when the user has already selected some filters and the query returns no organizations.
          return [...prevState, ...updatedData];
        });
      }
    } catch (error: any) {
      setArrOptions([]);
    } finally {
      setIsOrganizationCountLoading(false);
    }
  };

  return (
    <>
      <BaseFilterComponent
        getFilter={getFilter}
        icon={<OrganizationIcon />}
        title={t('filters.organizations')}
        onOpen={handleOnOpenModal}
        onCancel={onCancel}
        onClearAll={clearAllSelected}
        inUse={!!filter}
        storedFilter={filter}
        printedValues={printValues}
        disableFilterButton={arrOptions?.length === 0}
        loading={isOrganizationCountLoading}
      >
        <SelectionFilter
          values={optionValues}
          onChange={onChangeMultiSelectionFnc}
          multiselect
          loading={isOrganizationCountLoading}
          searchInputPlaceholder={t('filters.searchFilterPlaceholder', { param: 'organizations' })}
          displayCount={!inMobile(width)}
          reverseCheckboxOrder={inMobile(width)}
          checkboxSizeInPx={inMobile(width) ? 24 : undefined}
        />
      </BaseFilterComponent>
    </>
  );
};

export default AddOrganizationsFilter;
